import { useMemo, useRef } from 'react';

import type { NCalendars } from '@app/components/Calendars/types';
import { useResize } from '@app/hooks';
import { useTaskListContext } from '@app/providers/TaskListProvider';

export const useTaskList = ({ day, month }: NCalendars.DayDate) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { monthlyTasks, onMouseEnter, openPreviewTaskModal } =
    useTaskListContext();
  const dimensions = useResize(containerRef);

  const dailyTask = useMemo(
    () =>
      (monthlyTasks[`${month}-${day}`] || []).sort(
        ({ position }) => position
      ) || [],
    [day, monthlyTasks, month]
  );

  const taskHeight = 24;
  const taskGap = 4;
  const moreButtonHeight = 22;

  const maxPosition = useMemo(() => {
    if (dailyTask.length === 0) return 0;
    return dailyTask.sort((a, b) => b.position - a.position)[0].position;
  }, [dailyTask]);

  const isAllTasksView = useMemo(() => {
    if (dailyTask.length === 0) return true;
    const fullHeight = maxPosition * taskHeight + (maxPosition - 1) * taskGap;
    return fullHeight < dimensions.height - moreButtonHeight;
  }, [dimensions.height, dailyTask, maxPosition]);

  const showMainTasksNumber = useMemo(() => {
    if (isAllTasksView) return maxPosition;
    return Math.floor((dimensions.height - moreButtonHeight + 4) / 28);
  }, [maxPosition, dimensions.height, isAllTasksView]);

  const hasExtraTasks = useMemo(
    () =>
      dailyTask.find(({ position }) => position > showMainTasksNumber) !==
      undefined,
    [showMainTasksNumber, dailyTask]
  );

  const mainTaskList = useMemo(() => {
    const tasks = hasExtraTasks
      ? dailyTask.filter(({ position }) => position <= showMainTasksNumber)
      : dailyTask;

    return tasks;
  }, [hasExtraTasks, dailyTask, showMainTasksNumber]);

  const extraTaskList = useMemo(() => {
    const tasks = hasExtraTasks
      ? dailyTask.filter((task) => !mainTaskList.includes(task))
      : [];
    return tasks;
  }, [dailyTask, hasExtraTasks, mainTaskList]);

  return {
    containerRef,
    dimensions,
    mainTaskList,
    extraTaskList,
    hasExtraTasks,
    openPreviewTaskModal,
    onMouseEnter,
  };
};
